/**
 * El documento, en pantalla.
 *
 * Es la hoja tal cual va a salir impresa: mismo modelo, mismos colores, mismo
 * orden de bloques. No conmuta con el tema de la app — la hoja es blanca en
 * claro y en oscuro, igual que el papel. Un reporte que se ve distinto antes y
 * después de descargarlo no es una previsualización, es otra pantalla.
 */

import { LOGO_WAYAK } from "./imagenes";
import { arco, barrasDelDia, TINTA, TINTA_FACTURA as F, tintaDeEstado, TONO_FACTURA, TONO_TINTA } from "./geometria";
import type { Bloque, Campo, DatoResumen, DocumentoReporte, Reloj } from "./tipos";

const VERSALES = "uppercase tracking-[0.1em]";
/** El espaciado del comprobante (0.5 pt): bastante menor que el de los reportes anteriores. */
const VERSALES_FACTURA = "uppercase tracking-[0.05em]";

function Rotulo({ children, factura }: { children: string; factura?: boolean }) {
  return (
    <h3
      className={`${factura ? VERSALES_FACTURA : VERSALES} mb-2 border-b pb-1.5 text-[10px] font-bold`}
      style={{ color: factura ? F.tenue : TINTA.azul, borderColor: factura ? F.borde : TINTA.linea }}
    >
      {children}
    </h3>
  );
}

function Pie({ children }: { children: string }) {
  return (
    <p className="mt-1.5 text-[10px] leading-relaxed" style={{ color: TINTA.tenue }}>
      {children}
    </p>
  );
}

function Resumen({ datos, factura }: { datos: DatoResumen[]; factura?: boolean }) {
  return (
    <div
      className={`mb-5 flex flex-wrap gap-6 border px-5 py-4 ${factura ? "rounded-xl" : "rounded-lg"}`}
      style={factura ? { background: F.tarjeta, borderColor: F.borde } : { background: "#F5F8FC", borderColor: "#DCE6F2" }}
    >
      {datos.map((d) => (
        <div key={d.etiqueta} className="min-w-[110px] flex-1">
          <div className={`${factura ? VERSALES_FACTURA : VERSALES} text-[9px]`} style={{ color: factura ? F.tenue : TINTA.gris }}>
            {d.etiqueta}
          </div>
          <div
            className="font-mono text-[20px] font-extrabold leading-tight"
            style={{
              color: factura
                ? d.tono && d.tono !== "neutral" ? TONO_FACTURA[d.tono] : F.navy
                : d.tono ? TONO_TINTA[d.tono] : TINTA.navy,
            }}
          >
            {d.valor}
          </div>
          {d.nota && (
            <div className="text-[10px] italic" style={{ color: TINTA.tenue }}>
              {d.nota}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** El aro. Tres estados: parcial, lleno y punteado — ver `arco()`. */
function Aro({ reloj, radio = 13 }: { reloj: Reloj; radio?: number }) {
  const { avance, vuelta, modo } = arco(reloj.fraccion, reloj.valor, radio);
  const color = TONO_TINTA[reloj.tono ?? "info"];
  const lado = (radio + 3) * 2;
  return (
    <svg
      viewBox={`0 0 ${lado} ${lado}`}
      className="h-8 w-8 shrink-0 -rotate-90"
      aria-hidden="true"
    >
      <circle cx={lado / 2} cy={lado / 2} r={radio} fill="none" stroke="#E7EBF1" strokeWidth="2.6" />
      <circle
        cx={lado / 2}
        cy={lado / 2}
        r={radio}
        fill="none"
        stroke={modo === "vacio" ? TINTA.tenue : color}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeDasharray={modo === "vacio" ? "2 4" : `${avance} ${vuelta}`}
      />
    </svg>
  );
}

function Relojes({ relojes, factura }: { relojes: Reloj[]; factura?: boolean }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {relojes.map((r) => (
        <div
          key={r.etiqueta}
          className={`flex min-w-[140px] flex-1 items-center gap-2.5 border px-3 py-2.5 ${factura ? "rounded-xl" : "rounded-lg bg-white"}`}
          style={factura ? { background: F.tarjeta, borderColor: F.borde } : { borderColor: TINTA.linea }}
        >
          <Aro reloj={r} />
          <div className="min-w-0">
            <div className={`${factura ? VERSALES_FACTURA : VERSALES} text-[9px] leading-tight`} style={{ color: factura ? F.tenue : TINTA.gris }}>
              {r.etiqueta}
            </div>
            <div className="font-mono text-[15px] font-extrabold leading-tight" style={{ color: factura ? F.navy : TINTA.navy }}>
              {r.valor ?? "—"}
            </div>
            {r.nota && (
              <div className="text-[9px] italic" style={{ color: TINTA.tenue }}>
                {r.nota}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Los campos como la tarjeta «Información del pago» del comprobante: dos columnas, etiqueta a la izquierda. */
function CamposTarjeta({ titulo, campos }: { titulo?: string; campos: Campo[] }) {
  const porCol = Math.ceil(campos.length / 2);
  const columnas = [campos.slice(0, porCol), campos.slice(porCol)];
  return (
    <div className="mb-5 rounded-xl border px-4 py-3.5" style={{ background: F.tarjeta, borderColor: F.borde }}>
      {titulo && (
        <h3 className={`${VERSALES_FACTURA} mb-2.5 text-[10px] font-bold`} style={{ color: F.tenue }}>
          {titulo}
        </h3>
      )}
      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        {columnas.map((col, i) => (
          <dl key={i} className="space-y-1.5">
            {col.map((c) => (
              <div key={c.etiqueta} className="flex items-baseline gap-2 text-[11px]">
                <dt className="w-[150px] shrink-0 truncate" style={{ color: F.tenue }}>
                  {c.etiqueta}
                </dt>
                <dd
                  className={`min-w-0 truncate font-bold ${c.mono ? "font-mono text-[10px]" : ""}`}
                  style={{ color: F.texto }}
                >
                  {c.valor}
                </dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    </div>
  );
}

function Campos({ campos, foto }: { campos: Campo[]; foto?: string | null }) {
  return (
    <div className="flex items-start gap-4">
      {foto && (
        <img
          src={foto}
          alt=""
          /* Decorativa: el nombre va escrito al lado, así que un lector de
             pantalla que lea «foto de perfil» sólo repetiría el campo. */
          className="h-[86px] w-[86px] shrink-0 rounded-lg object-cover"
          style={{ border: `1px solid ${TINTA.linea}` }}
        />
      )}
      <div className="flex flex-1 flex-wrap gap-x-9 gap-y-2.5">
        {campos.map((c) => (
          <div key={c.etiqueta} className="min-w-[150px]">
            <div className={`${VERSALES} text-[9px]`} style={{ color: TINTA.gris }}>
              {c.etiqueta}
            </div>
            <div
              className={`text-[12px] font-bold ${c.mono ? "font-mono text-[11px] font-medium" : ""}`}
              style={{ color: TINTA.texto }}
            >
              {c.valor}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tabla({ bloque, factura }: { bloque: Extract<Bloque, { tipo: "tabla" }>; factura?: boolean }) {
  /*
    ── Por qué 60 filas y no un paginador ──────────────────────────────────
    Esto es la PREVISUALIZACIÓN de una hoja: sirve para confirmar que las
    columnas son las que se quieren y que los datos se ven bien, no para
    consultar los 84 mil puntos. Un paginador aquí invita a usar la vista
    previa como tabla de trabajo —que ya existe, en la sección— y además
    tendría que repintar miles de nodos en cada cambio de página.
  */
  const TOPE = 60;
  const filas = bloque.filas.slice(0, TOPE);
  const restantes = bloque.filas.length - filas.length;

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr>
              {bloque.columnas.map((c) => (
                <th
                  key={c.clave}
                  className={`${factura ? VERSALES_FACTURA : VERSALES} whitespace-nowrap px-2.5 py-1.5 text-[9px] font-bold ${
                    c.alinea === "derecha" ? "text-right" : c.alinea === "centro" ? "text-center" : "text-left"
                  }`}
                  style={factura ? { background: F.navy, color: "#FFFFFF" } : { background: TINTA.azulClaro, color: TINTA.navy }}
                >
                  {c.titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={i} style={{ background: i % 2 ? (factura ? F.tarjeta : TINTA.cebra) : "transparent" }}>
                {bloque.columnas.map((c) => (
                  <td
                    key={c.clave}
                    className={`px-2.5 py-1 align-top ${
                      c.alinea === "derecha" ? "text-right font-mono tabular-nums" : c.alinea === "centro" ? "text-center" : ""
                    } ${c.mono ? "font-mono text-[10px]" : ""}`}
                    style={{
                      borderBottom: `1px solid ${factura ? F.bordeSuave : TINTA.lineaSuave}`,
                      color: factura ? F.texto : TINTA.texto,
                    }}
                  >
                    {f[c.clave] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {restantes > 0 && (
        <Pie>{`Se muestran las primeras ${filas.length} filas de ${bloque.filas.length.toLocaleString("es-MX")}. El archivo las lleva todas.`}</Pie>
      )}
    </>
  );
}

function Regla({ bloque, factura }: { bloque: Extract<Bloque, { tipo: "regla" }>; factura?: boolean }) {
  return (
    <>
      <div className="mb-2.5 flex flex-wrap gap-x-4 gap-y-1">
        {bloque.leyenda.map((item) => (
          <span key={item.estado} className="flex items-center gap-1.5 text-[10px]" style={{ color: factura ? F.tenue : TINTA.gris }}>
            <span className="h-2 w-2 rounded-sm" style={{ background: tintaDeEstado(item.estado) }} />
            {item.etiqueta}
          </span>
        ))}
      </div>
      {bloque.dias.map((dia) => (
        <div key={`${dia.inicio}-${dia.etiqueta}`} className="mb-2.5">
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="text-[11px] font-bold" style={{ color: factura ? F.texto : TINTA.navy }}>
              {dia.etiqueta}
            </span>
            {dia.cifras && (
              <span className="font-mono text-[10px] tabular-nums" style={{ color: factura ? F.tenue : TINTA.gris }}>
                {dia.cifras}
              </span>
            )}
          </div>
          <div className="relative h-4 overflow-hidden rounded" style={{ background: factura ? F.bordeSuave : "#EEF1F5" }}>
            {barrasDelDia(dia.tramos, dia.inicio).map((b, i) => (
              <span
                key={i}
                className="absolute top-0 h-full"
                style={{ left: `${b.izquierda}%`, width: `${b.ancho}%`, background: tintaDeEstado(b.estado) }}
              />
            ))}
          </div>
          <div className="mt-0.5 flex justify-between font-mono text-[8px]" style={{ color: factura ? F.tenue : TINTA.tenue }}>
            {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
              <span key={h}>{String(h).padStart(2, "0")}</span>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function Nota({ bloque, factura }: { bloque: Extract<Bloque, { tipo: "nota" }>; factura?: boolean }) {
  if (factura) {
    return (
      <div
        className="my-3 rounded-xl border px-4 py-3 text-[11px] leading-relaxed"
        style={{
          background: F.tarjeta,
          borderColor: F.borde,
          borderLeft: `3px solid ${TONO_FACTURA[bloque.tono ?? "aviso"]}`,
          color: F.textoSecundario,
        }}
      >
        {bloque.titulo && (
          <strong className="block" style={{ color: F.texto }}>
            {bloque.titulo}
          </strong>
        )}
        {bloque.texto}
      </div>
    );
  }
  return (
    <div
      className="my-3 rounded-r px-3.5 py-2.5 text-[11px] leading-relaxed"
      style={{ background: "#FFF9E8", borderLeft: `3px solid ${TONO_TINTA[bloque.tono ?? "aviso"]}`, color: "#6B5A2E" }}
    >
      {bloque.titulo && (
        <strong className="block" style={{ color: "#8A6D1F" }}>
          {bloque.titulo}
        </strong>
      )}
      {bloque.texto}
    </div>
  );
}

export interface VistaDocumentoProps {
  documento: DocumentoReporte;
  generado: string;
}

/** Banda y fila de meta con el diseño del PDF de facturación. */
function EncabezadoFactura({ documento, generado }: VistaDocumentoProps) {
  return (
    <>
      <header className="flex items-center justify-between gap-4 px-6 py-6" style={{ background: F.navy }}>
        <img src={LOGO_WAYAK} alt="WAYAK" className="h-[72px] w-auto -my-3" />
        <div className="text-right">
          <h2 className={`${VERSALES_FACTURA} text-[20px] font-bold leading-tight text-white`}>{documento.titulo}</h2>
          <p className="mt-1 text-[11px]" style={{ color: F.subtituloBanda }}>
            {documento.folio}
          </p>
          {documento.sello && (
            <span
              className={`${VERSALES_FACTURA} mt-2 inline-block rounded-full px-3 py-1 text-[9px] font-bold text-white`}
              style={{ background: F.primario }}
            >
              {documento.sello}
            </span>
          )}
        </div>
      </header>
      <div style={{ height: 3, background: F.primario }} />
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 px-6 pt-5">
        <div className="flex flex-wrap gap-x-10 gap-y-2">
          <div>
            <div className={`${VERSALES_FACTURA} text-[9px]`} style={{ color: F.tenue }}>
              Cliente · Unidad
            </div>
            <div className="text-[12px] font-bold" style={{ color: F.texto }}>
              {documento.subtitulo || "—"}
            </div>
          </div>
          <div>
            <div className={`${VERSALES_FACTURA} text-[9px]`} style={{ color: F.tenue }}>
              Fecha de emisión
            </div>
            <div className="text-[12px] font-bold" style={{ color: F.texto }}>
              {generado}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function VistaDocumento({ documento, generado }: VistaDocumentoProps) {
  const factura = documento.estilo === "factura";
  return (
    <article
      className={`mx-auto w-full max-w-[210mm] overflow-hidden rounded-lg bg-white shadow-lg ${
        // El comprobante va en Helvetica de principio a fin, también lo «mono».
        factura ? "[&_.font-mono]:![font-family:inherit]" : ""
      }`}
      style={{ color: TINTA.texto, ...(factura ? { fontFamily: "Helvetica, Arial, sans-serif" } : {}) }}
      data-testid="vista-documento"
    >
      {factura ? (
        <EncabezadoFactura documento={documento} generado={generado} />
      ) : (
        <>
      <header
        className="flex items-start justify-between gap-4 px-6 py-5"
        style={{ background: TINTA.navy }}
      >
        <div>
          <div className="text-[22px] font-extrabold leading-none tracking-[0.3em] text-white">
            W<span style={{ color: TINTA.cian }}>A</span>Y<span style={{ color: TINTA.cian }}>A</span>K
          </div>
          <div className="mt-1 text-[7px] font-medium tracking-[0.5em]" style={{ color: "#B7C4D4" }}>
            LOGISTICS
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-[18px] font-bold leading-tight text-white">{documento.titulo}</h2>
          <p className="mt-1 text-[10px]" style={{ color: "#9FB3C8" }}>
            {documento.folio}
            <br />
            {documento.subtitulo}
          </p>
          {documento.sello && (
            <span
              className={`${VERSALES} mt-2 inline-block rounded-full px-3 py-1 text-[9px] font-bold text-white`}
              style={{ background: TINTA.verde }}
            >
              {documento.sello}
            </span>
          )}
        </div>
      </header>
      <div style={{ height: 5, background: TINTA.azul }} />
        </>
      )}

      <div className="px-6 py-5">
        {documento.bloques.map((b, i) => {
          switch (b.tipo) {
            case "resumen":
              return <Resumen key={i} datos={b.datos} factura={factura} />;
            case "relojes":
              return (
                <section key={i} className="mb-5">
                  {b.titulo && <Rotulo factura={factura}>{b.titulo}</Rotulo>}
                  <Relojes relojes={b.relojes} factura={factura} />
                  {b.pie && <Pie>{b.pie}</Pie>}
                </section>
              );
            case "campos":
              if (factura && !b.foto) return <CamposTarjeta key={i} titulo={b.titulo} campos={b.campos} />;
              return (
                <section key={i} className="mb-5">
                  {b.titulo && <Rotulo factura={factura}>{b.titulo}</Rotulo>}
                  <Campos campos={b.campos} foto={b.foto} />
                </section>
              );
            case "tabla":
              return (
                <section key={i} className="mb-5">
                  {b.titulo && <Rotulo factura={factura}>{b.titulo}</Rotulo>}
                  <Tabla bloque={b} factura={factura} />
                  {b.pie && <Pie>{b.pie}</Pie>}
                </section>
              );
            case "regla":
              return (
                <section key={i} className="mb-5">
                  {b.titulo && <Rotulo factura={factura}>{b.titulo}</Rotulo>}
                  <Regla bloque={b} factura={factura} />
                  {b.pie && <Pie>{b.pie}</Pie>}
                </section>
              );
            case "nota":
              return <Nota key={i} bloque={b} factura={factura} />;
            case "total":
              return (
                <div
                  key={i}
                  className="my-4 flex items-center justify-between gap-4 rounded-lg px-5 py-3.5 text-white"
                  style={{ background: factura ? F.navy : TINTA.azul }}
                >
                  <span className={`${factura ? VERSALES_FACTURA : VERSALES} text-[11px] font-bold`}>{b.etiqueta}</span>
                  <span className="font-mono text-[17px] font-extrabold">{b.valor}</span>
                </div>
              );
            case "salto":
              return (
                <hr key={i} className="my-6 border-dashed" style={{ borderColor: TINTA.linea }} />
              );
            default:
              return null;
          }
        })}
      </div>

      <footer
        className="flex items-start justify-between gap-4 border-t px-6 py-3 text-[9px]"
        style={{ borderColor: factura ? F.borde : TINTA.linea, color: factura ? F.tenue : TINTA.gris }}
      >
        <div>
          WAYAK Logistics · Generado el {generado}
          <div style={{ color: factura ? F.tenue : TINTA.tenue }}>
            Reporte de operación emitido por WAYAK Logistics. No constituye dictamen pericial ni
            prueba judicial por sí solo.
          </div>
        </div>
      </footer>
    </article>
  );
}
